import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RealtimeChannel } from "@supabase/supabase-js";

interface MatchSyncState {
  isSynced: boolean;
  isOtherOnline: boolean;
  shouldStartAnimation: boolean;
}

/**
 * Hook to synchronize match celebration animation between two users in real-time.
 * Uses Supabase Realtime broadcast to coordinate timing.
 */
export const useMatchSync = (
  userId: string | undefined,
  matchedUserId: string | undefined,
  isOpen: boolean
) => {
  const [state, setState] = useState<MatchSyncState>({
    isSynced: false,
    isOtherOnline: false,
    shouldStartAnimation: false,
  });
  
  const channelRef = useRef<RealtimeChannel | null>(null);
  const readyReceivedRef = useRef(false);
  const sentReadyRef = useRef(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create a consistent channel name (sorted IDs)
  const getChannelName = useCallback(() => {
    if (!userId || !matchedUserId) return null;
    const ids = [userId, matchedUserId].sort();
    return `match-sync:${ids[0]}:${ids[1]}`;
  }, [userId, matchedUserId]);

  // Handle sync logic
  const triggerSync = useCallback(() => {
    console.log("[MatchSync] Both users ready, triggering sync");
    setState(prev => ({
      ...prev,
      isSynced: true,
      shouldStartAnimation: true,
    }));
  }, []);

  // Setup channel and sync
  useEffect(() => {
    if (!isOpen || !userId || !matchedUserId) {
      // Reset state when modal closes
      setState({
        isSynced: false,
        isOtherOnline: false,
        shouldStartAnimation: false,
      });
      readyReceivedRef.current = false;
      sentReadyRef.current = false;
      return;
    }

    const channelName = getChannelName();
    if (!channelName) return;

    console.log("[MatchSync] Joining channel:", channelName);

    // Cleanup existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Create new channel
    const channel = supabase.channel(channelName);
    channelRef.current = channel;

    // Listen for broadcast messages
    channel.on("broadcast", { event: "match_ready" }, (payload) => {
      console.log("[MatchSync] Received ready from:", payload.payload?.userId);
      
      if (payload.payload?.userId !== userId) {
        readyReceivedRef.current = true;
        setState(prev => ({ ...prev, isOtherOnline: true }));

        // If we already sent our ready, trigger sync
        if (sentReadyRef.current) {
          triggerSync();
        }
      }
    });

    // Listen for sync trigger (backup synchronization)
    channel.on("broadcast", { event: "match_sync_start" }, () => {
      console.log("[MatchSync] Received sync start signal");
      setState(prev => ({ ...prev, shouldStartAnimation: true, isSynced: true }));
    });

    // Subscribe to channel
    channel.subscribe(async (status) => {
      console.log("[MatchSync] Channel status:", status);
      
      if (status === "SUBSCRIBED") {
        // Small delay to ensure channel is fully ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Send our ready signal
        console.log("[MatchSync] Sending ready signal");
        sentReadyRef.current = true;
        
        await channel.send({
          type: "broadcast",
          event: "match_ready",
          payload: { userId, timestamp: Date.now() },
        });

        // If other user already sent ready, trigger sync
        if (readyReceivedRef.current) {
          // Broadcast sync start to ensure both see it at the same time
          await channel.send({
            type: "broadcast",
            event: "match_sync_start",
            payload: { timestamp: Date.now() },
          });
          triggerSync();
        }

        // Fallback: If no sync after 2 seconds, start animation anyway
        // (other user might be offline)
        syncTimeoutRef.current = setTimeout(() => {
          console.log("[MatchSync] Timeout - starting animation solo");
          setState(prev => ({ ...prev, shouldStartAnimation: true }));
        }, 2000);
      }
    });

    return () => {
      console.log("[MatchSync] Cleaning up channel");
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isOpen, userId, matchedUserId, getChannelName, triggerSync]);

  return state;
};
